import { createColumnHelper, type ColumnDef } from '@tanstack/react-table';
import { Link } from 'react-router-dom';
import { ChevronDown, ChevronRight, Eye, RefreshCw, ArrowUpDown, ArrowUp, ArrowDown, AlertTriangle, ExternalLink, Loader2 } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ReceivingStatusBadge } from './ReceivingStatusBadge';
import { ItemTypeBadge } from './ItemTypeBadge';
import type { ReceivingTableRow } from '../types/receiving.types';
import { ReceivingStatusEnum, PostingStatusEnum, DeliveryTypeEnum } from '../types/receiving.types';

function formatTimeAgo(dateStr: string): string {
  const now = new Date();
  const date = new Date(dateStr);
  const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (seconds < 60) return 'Just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months}mo ago`;
  const years = Math.floor(months / 12);
  return `${years}y ago`;
}

const DELIVERY_TYPE_LABELS: Record<DeliveryTypeEnum, string> = {
  [DeliveryTypeEnum.Local]: 'Local',
  [DeliveryTypeEnum.ImportRawMaterials]: 'Import – Raw Materials',
  [DeliveryTypeEnum.ImportSpareParts]: 'Import – Spare Parts',
  [DeliveryTypeEnum.Projects]: 'Projects',
};

interface ColumnOptions {
  onView: (docId: number) => void;
  onResync: (e: React.MouseEvent, docId: number) => void;
  isSubmitting: boolean;
  tab: 'open' | 'closed';
  /** 86d3rrgt5 — user has receiving.create (Resync's permission); the affordance is hidden otherwise. */
  canResync: boolean;
}

const columnHelper = createColumnHelper<ReceivingTableRow>();

function SortableHeader({ label, column }: { label: string; column: { getIsSorted: () => false | 'asc' | 'desc'; getToggleSortingHandler: () => ((event: unknown) => void) | undefined } }) {
  const sorted = column.getIsSorted();
  return (
    <Button
      variant="ghost"
      size="sm"
      className="-ml-3 h-8 data-[state=open]:bg-accent"
      onClick={column.getToggleSortingHandler()}
    >
      {label}
      {sorted === 'asc' ? (
        <ArrowUp className="ml-1 h-3.5 w-3.5" />
      ) : sorted === 'desc' ? (
        <ArrowDown className="ml-1 h-3.5 w-3.5" />
      ) : (
        <ArrowUpDown className="ml-1 h-3.5 w-3.5 text-muted-foreground/50" />
      )}
    </Button>
  );
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function createReceivingColumns(options: ColumnOptions): ColumnDef<ReceivingTableRow, any>[] {
  return [
    // Expand/collapse column
    columnHelper.display({
      id: 'expand',
      header: '',
      size: 40,
      enableSorting: false,
      cell: ({ row }) => {
        if (row.original.rowType !== 'document') return null;
        if (!row.getCanExpand()) return null;

        return (
          <div className="flex items-center justify-center h-6 w-6">
            {row.getIsExpanded() ? (
              <ChevronDown className="h-4 w-4" />
            ) : (
              <ChevronRight className="h-4 w-4" />
            )}
          </div>
        );
      },
    }),

    // Document No / Item Code
    columnHelper.accessor(
      (row) => row.rowType === 'document' ? row.document.docNo : '',
      {
        id: 'identifier',
        header: ({ column }) => <SortableHeader label="Document / Item" column={column} />,
        cell: ({ row }) => {
          if (row.original.rowType === 'document') {
            const { document: doc } = row.original;
            return (
              <div className="space-y-1">
                <span className="font-mono font-medium">{doc.docNo}</span>
                {doc.forAdjustment && (
                  <div>
                    <Badge variant="outline" className="border-warning/30 text-warning text-xs">
                      <AlertTriangle className="mr-1 h-3 w-3" />
                      For Adjustment
                    </Badge>
                  </div>
                )}
              </div>
            );
          }
          return (
            <div className="pl-6">
              <span className="font-mono text-sm">{row.original.item.itemCode}</span>
              <p className="text-xs text-muted-foreground truncate max-w-[200px]">
                {row.original.item.itemDescription}
              </p>
            </div>
          );
        },
      },
    ),

    // PO Number / Vendor
    columnHelper.accessor(
      (row) => row.rowType === 'document' ? row.document.purchaseOrderNumber : '',
      {
        id: 'poNumber',
        header: ({ column }) => <SortableHeader label="PO / Vendor" column={column} />,
        size: 200,
        cell: ({ row }) => {
          if (row.original.rowType === 'document') {
            const { document: doc } = row.original;
            return (
              <div>
                {doc.purchaseOrderId ? (
                  <Link
                    to={`/mro/purchase-orders/${doc.purchaseOrderId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="inline-flex items-center gap-1 text-sm font-medium text-foreground underline underline-offset-2 decoration-muted-foreground/50 hover:decoration-foreground transition-colors"
                  >
                    {doc.purchaseOrderNumber}
                    <ExternalLink className="h-3 w-3 flex-shrink-0" />
                  </Link>
                ) : (
                  <span className="text-sm font-medium">{doc.purchaseOrderNumber}</span>
                )}
                <p className="text-xs text-muted-foreground truncate max-w-[200px]">
                  {doc.vendorName}
                </p>
              </div>
            );
          }
          const { item } = row.original;
          return (
            <div className="pl-6 flex gap-1">
              <ItemTypeBadge item={item} className="text-xs" />
            </div>
          );
        },
      },
    ),

    // Delivery
    columnHelper.display({
      id: 'delivery',
      header: 'Delivery',
      enableSorting: false,
      cell: ({ row }) => {
        if (row.original.rowType === 'document') {
          const { document: doc } = row.original;
          return (
            <div>
              <span className="text-sm">{doc.deliveryNumber}</span>
              <p className="text-xs text-muted-foreground">
                {DELIVERY_TYPE_LABELS[doc.deliveryType]}
              </p>
            </div>
          );
        }
        return null;
      },
    }),

    // Status
    columnHelper.accessor(
      (row) => row.rowType === 'document' ? row.document.receivingStatus : -1,
      {
        id: 'status',
        header: ({ column }) => <SortableHeader label="Status" column={column} />,
        cell: ({ row }) => {
          if (row.original.rowType === 'document') {
            const doc = row.original.document;
            return (
              <div className="flex flex-wrap items-center gap-1.5">
                <ReceivingStatusBadge status={doc.receivingStatus} />
                {doc.postingStatus === PostingStatusEnum.Posting && (
                  <Badge className="gap-1 bg-info/10 text-info hover:bg-info/10">
                    <Loader2 className="h-3 w-3 animate-spin" />
                    Posting…
                  </Badge>
                )}
                {doc.postingStatus === PostingStatusEnum.PostingFailed && (
                  <Badge className="gap-1 bg-destructive/10 text-destructive hover:bg-destructive/10">
                    <AlertTriangle className="h-3 w-3" />
                    Failed
                  </Badge>
                )}
              </div>
            );
          }
          const { item } = row.original;
          return (
            <div className="pl-6">
              {item.isPutAway ? (
                <Badge variant="success" className="text-xs">Put Away</Badge>
              ) : (
                <Badge variant="secondary" className="text-xs">Pending</Badge>
              )}
            </div>
          );
        },
      },
    ),

    // Quantity / Progress
    columnHelper.accessor(
      (row) => {
        if (row.rowType === 'document') {
          const items = row.document.items;
          return items.length > 0 ? items.filter((i) => i.isPutAway).length / items.length : 0;
        }
        return -1;
      },
      {
        id: 'quantity',
        header: ({ column }) => <SortableHeader label="Quantity" column={column} />,
        cell: ({ row }) => {
          if (row.original.rowType === 'document') {
            const items = row.original.document.items;
            const putAwayCount = items.filter((i) => i.isPutAway).length;
            const total = items.length;
            const percentage = total > 0 ? (putAwayCount / total) * 100 : 0;
            return (
              <div className="space-y-1">
                <span className="text-sm">
                  {putAwayCount}/{total} items
                </span>
                <div className="w-20 h-2 bg-border rounded-full overflow-hidden">
                  <div
                    className="h-full bg-primary transition-all"
                    style={{ width: `${percentage}%` }}
                  />
                </div>
              </div>
            );
          }
          const { item } = row.original;
          if (item.isServiceItem) {
            return (
              <div className="pl-6 text-sm text-muted-foreground">
                {item.receivedQuantity} {item.unitOfMeasure}
                <span className="ml-1 text-xs">(N/A)</span>
              </div>
            );
          }
          return (
            <div className="pl-6 text-sm">
              <span className={item.isPutAway ? 'text-success font-medium' : ''}>
                {item.quantityPutAway}
              </span>
              <span className="text-muted-foreground">
                /{item.receivedQuantity} {item.unitOfMeasure}
              </span>
            </div>
          );
        },
      },
    ),

    // Received By
    columnHelper.accessor(
      (row) => row.rowType === 'document' ? row.document.receivedByName : '',
      {
        id: 'receivedBy',
        header: ({ column }) => <SortableHeader label="Received By" column={column} />,
        cell: ({ row }) => {
          if (row.original.rowType === 'document') {
            const { document: doc } = row.original;
            return (
              <div>
                <span className="text-sm">{doc.receivedByName}</span>
                {options.tab === 'open' ? (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <p className="text-xs text-muted-foreground cursor-help underline decoration-dotted decoration-muted-foreground/40 underline-offset-2 hover:decoration-muted-foreground">
                          {formatTimeAgo(doc.receivedAt)}
                        </p>
                      </TooltipTrigger>
                      <TooltipContent>
                        {new Date(doc.receivedAt).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true })}
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                ) : (
                  <p className="text-xs text-muted-foreground">
                    {new Date(doc.receivedAt).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true })}
                  </p>
                )}
              </div>
            );
          }
          return null;
        },
      },
    ),

    // Actions
    columnHelper.display({
      id: 'actions',
      header: '',
      size: 100,
      enableSorting: false,
      cell: ({ row }) => {
        if (row.original.rowType !== 'document') return null;
        const { document: doc } = row.original;
        const showResync = options.canResync && doc.forAdjustment && (
          doc.receivingStatus === ReceivingStatusEnum.PendingPutAway ||
          doc.receivingStatus === ReceivingStatusEnum.PartiallyPutAway
        );

        return (
          <div className="flex gap-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                options.onView(doc.id);
              }}
              title="View details"
            >
              <Eye className="h-4 w-4" />
            </Button>
            {showResync && (
              <Button
                variant="ghost"
                size="sm"
                disabled={options.isSubmitting}
                onClick={(e) => {
                  e.stopPropagation();
                  options.onResync(e, doc.id);
                }}
                title="Resync with PO"
              >
                <RefreshCw className="h-4 w-4" />
              </Button>
            )}
          </div>
        );
      },
    }),
  ];
}
